import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Textarea,
  VStack,
  chakra,
  Icon,
} from "@chakra-ui/react";
import { useState } from "react";
import { useAPI } from "@/contexts/APIContext";
import PageInfo from "@/components/PageInfo";
import { useForm } from "react-hook-form";
import { EmailIcon } from "@chakra-ui/icons";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LinkButton, SkewedHeader } from "@/components/shared/generic";
import { FaDiscord } from "react-icons/fa";

const ContactForm = ({ onSubmitForm, loading }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  return (
    <Box mt={8}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <VStack>
          {/* NAME */}
          <FormControl isInvalid={!!errors.name}>
            <FormLabel>Full Name</FormLabel>
            <Input
              type="text"
              placeholder="Ethan Zhang"
              {...register("name", { required: "Please enter your name." })}
            />
            {errors.name && <FormErrorMessage>{errors.name.message}</FormErrorMessage>}
          </FormControl>

          {/* EMAIL */}
          <FormControl isInvalid={!!errors.email}>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              placeholder="ethanzhang@gmail.com"
              {...register("email", { required: "Please enter an email address." })}
            />
            {errors.email ? (
              <FormErrorMessage>{errors.email.message}</FormErrorMessage>
            ) : (
              <FormHelperText>
                For a more helpful reply, tell us the email associated with your Admit Yogi account.
              </FormHelperText>
            )}
          </FormControl>

          {/* SUBJECT */}
          <FormControl isInvalid={!!errors.subject}>
            <FormLabel>Subject</FormLabel>
            <Input
              type="text"
              placeholder="I have a question about the website"
              {...register("subject", { required: "Please enter a subject." })}
            />
            {errors.subject && <FormErrorMessage>{errors.subject.message}</FormErrorMessage>}
          </FormControl>

          {/* BODY */}
          <FormControl isInvalid={!!errors.body}>
            <FormLabel>Message</FormLabel>
            <Textarea
              placeholder="What parts of an application do I get access to when I purchase a profile?"
              cols={8}
              {...register("body", { required: "Please enter a message." })}
            />
            {errors.body && <FormErrorMessage>{errors.body.message}</FormErrorMessage>}
          </FormControl>
          <Button colorScheme="black" type="submit" isLoading={loading} leftIcon={<EmailIcon />}>
            Submit
          </Button>
        </VStack>
      </form>
    </Box>
  );
};

const SuccessAlert = () => (
  <Alert status="success">
    <AlertIcon />
    Successfully sent your message! We&apos;ll get back to you as soon as possible — check your
    inbox, including your spam folder.
  </Alert>
);

const ErrorAlert = () => (
  <Alert status="error">
    <AlertIcon />
    Uh oh! There was an error processing your message. Shoot an email to contact@admityogi.com
    instead.
  </Alert>
);

export default function ContactUs() {
  const api = useAPI();

  const [success, setSuccess] = useState(false);
  const [failure, setFailure] = useState(false);
  const [loading, setLoading] = useState(false);

  const send = async (data) => {
    setSuccess(false);
    setFailure(false);

    try {
      setLoading(true);
      await api.postUnprotected("send-mail", undefined, data);
      setSuccess(true);
    } catch (e) {
      console.error(e);
      setFailure(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageInfo title="Contact Us" themeColor="white" />
      <Navbar />
      <chakra.h1 mt={10} textStyle="pageHeader" textAlign="center">
        Contact Us
      </chakra.h1>
      <Container maxW="container.sm" my={10}>
        <Box layerStyle="md">
          <p>
            We&apos;re a new company with new ideas. And we want you to be a part of them. If you
            encounter any difficulties creating an account, stumble across a website bug, or have
            any suggestions for how we can improve our site, let us know!
          </p>
          <LinkButton
            href="https://discord.gg/3ca7ZzwXJR"
            target="_blank"
            colorScheme="blue"
            mt={4}
            size="lg"
            leftIcon={<Icon as={FaDiscord} />}
          >
            Join our Discord Community
          </LinkButton>
          <p>
            <b>Email:&nbsp;</b>
            <a href="mailto:contact@admityogi.com">contact@admityogi.com</a>
          </p>
          <p>
            <b>Request a Refund:&nbsp;</b>
            <a href="/refund">View our refund form</a>
          </p>
        </Box>
        {success && <SuccessAlert />}
        {failure && <ErrorAlert />}
        {!success && !failure && <ContactForm onSubmitForm={send} loading={loading} />}
      </Container>
      <Footer />
    </>
  );
}
